import { computed } from "vue";
import { useProductEditorStore } from "@/stores/productEditor";

/**
 * Resolve the ADR 0015 client scope for an EditorArtworkPicker.
 *
 * ADR 0015 ("Library scoping in the product editor"): the decoration
 * picker inside a product editor is "filtered by default to the store's
 * client -- Samsung's store sees Samsung's approved decorations", with an
 * "All decorations" toggle that lifts the filter.
 *
 * Why a composable rather than two inline computeds
 * -------------------------------------------------
 * Written for two product-editor callsites that had to agree:
 * DecorationInspector's "Replace" flow and AddSpecificVariantModal's
 * artwork step. #2855 removed the latter along with the rest of the
 * per-SKU specific-colors authoring UI, so there is one consumer today.
 * The composable stays because the drift it prevents is real -- if one
 * surface filters and another does not, a Builder sees a different
 * library depending on which button opened the picker.
 *
 * Where the client comes from
 * ---------------------------
 * The product-editor route carries only ``:campaignProductId``
 * (``router/index.js``), and the productEditor store holds no store or
 * client id of its own. The owning store's client rides on the
 * campaign_product payload the editor already loads, as the read-only
 * denormalized attribute ``store_client_id`` -- the same pattern as
 * ``store_name`` (consumed by EditorPagehead) and ``store_category_id``.
 * That avoids a second round trip to ``/stores/<id>``.
 *
 * NULL is a legitimate answer, not a failure: ``stores.client_id`` is
 * nullable ("existing campaigns predate the Client tenant"). The picker
 * reads NULL as "nothing to narrow to" and shows the whole tenant
 * library, which is exactly the pre-ADR-0015 behaviour.
 *
 * @returns {{clientId: import('vue').ComputedRef<number|string|null>,
 *            inUseArtworkIds: import('vue').ComputedRef<Array<string>>}}
 */
export function useEditorArtworkPickerScope() {
    const editor = useProductEditorStore();

    const clientId = computed(() => {
        const raw = editor.getEffectiveAttribute?.("store_client_id");
        if (raw == null || raw === "") return null;
        return raw;
    });

    /**
     * Artwork ids already applied to this product.
     *
     * These stay visible even when the client filter would exclude them.
     * Without this a Builder could SEE a decoration on the product while
     * the picker insisted it did not exist -- the editor would read as
     * broken, and there would be no way to re-pick the artwork already
     * in use (e.g. to apply it to another colour).
     *
     * Read off the loaded placements' variants rather than a dedicated
     * endpoint: ``loadServerState`` already includes
     * ``placements.variants``, so the ids are in memory. ``artwork_file_id``
     * is the free-form string that carries the Artwork row id (see
     * EditorArtworkPicker's "Identifier passed downstream" note).
     */
    const inUseArtworkIds = computed(() => {
        const included = editor.serverState?.included;
        if (!Array.isArray(included)) return [];
        const out = [];
        included.forEach((row) => {
            // Both spellings, matching the tolerance productEditor.js
            // already applies at lines 3585 / 3698 -- the included type
            // arrives as "Variant" or "variants" depending on the path.
            if (row?.type !== "Variant" && row?.type !== "variants") return;
            const raw = row?.attributes?.artwork_file_id;
            if (raw == null || raw === "") return;
            const sv = String(raw).trim();
            if (sv && !out.includes(sv)) out.push(sv);
        });
        return out;
    });

    return { clientId, inUseArtworkIds };
}
